// games/balda/useBaldaLogic.js
import { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';

export function useBaldaLogic(socket, options = {}) {
    const { t } = useTranslation();
    const storeState = useStore.getState();
    const userId = String(storeState.ref.telegram.telegramUserId);

    const [state, setState] = useState({ grid:[], gridSize: { rows: 5, cols: 5 }, score: 0, mode: 'solo', foundWords:[], definitionsMap: {}, lastMove: [] });
    const [duelData, setDuelData] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [shakeState, setShakeState] = useState(null);

    // ИНКАПСУЛЯЦИЯ ЛОГИКИ ОТОБРАЖЕНИЯ РЕЗУЛЬТАТОВ
    const [showResults, _setShowResults] = useState(false);
    const [finalData, _setFinalData] = useState(null);

    // Стейты для визуальных эффектов (перенесены из Balda.js)
    const [opponentAction, setOpponentAction] = useState(null);
    const [flashTurn, setFlashTurn] = useState(false);
    const [turnToastKey, setTurnToastKey] = useState(0);

    const hasDismissedRef = useRef(false);
    const finalDataRef = useRef(null);

    const setShowResults = useCallback((val) => {
        _setShowResults(val);
        if (val === false) hasDismissedRef.current = true;
    }, []);

    const setFinalData = useCallback((data) => {
        _setFinalData(data);
        finalDataRef.current = data;
    }, []);

    const [showDebug, setShowDebug] = useState(false);

    const [lastSyncTime, setLastSyncTime] = useState(Date.now());
    const [localTick, setLocalTick] = useState(0);

    // Refs для доступа к свежим данным внутри сокет-событий без пересоздания слушателя
    const stateRef = useRef(state);
    useEffect(() => { stateRef.current = state; }, [state]);
    
    const opponentTimerRef = useRef(null);
    const flashTimerRef = useRef(null);
    const prevGridRef = useRef([]);
    const isSpectator = useRef(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === '0') setShowDebug(prev => !prev);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (state.mode !== 'duel' || showResults) return;
        const timer = setInterval(() => setLocalTick(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, [state.mode, showResults]);

    const onSuccessRef = useRef(options.onSuccess);
    useEffect(() => {
        onSuccessRef.current = options.onSuccess;
    }, [options.onSuccess]);

    const isShakingLockRef = useRef(false);

    const triggerShake = useCallback((type = 'error') => {
        if (isShakingLockRef.current) return;
        isShakingLockRef.current = true;
        setShakeState(type);
        window.croco?.haptic?.(type === 'neutral' ? 'impactLight' : 'error');
        setTimeout(() => { setShakeState(null); isShakingLockRef.current = false; }, 400);
    }, []);

    const showFeedback = useCallback((type, msg) => {
        setStatusMessage({ type, text: msg });
        if (type === 'success') window.croco?.haptic?.('success');
        else window.croco?.haptic?.('warning');
        setTimeout(() => setStatusMessage(null), 1500);
    }, []);

    // АРХИТЕКТУРНОЕ ИСПРАВЛЕНИЕ №1: sendAction больше не зависит от глобального store
    // Мы получаем актуальный store только в момент вызова функции.
    // Это предотвращает бесконечные циклы отключения/подключения сокета.
    const sendAction = useCallback((actionType, payload = {}) => {
        const currentStore = useStore.getState();
        currentStore.ref.work.methods.hostAction('mafiaMiniGameAction', {
            type: actionType,
            userId: userId,
            ...payload
        });
    }, [userId]);

    // МГНОВЕННЫЙ ЗВУК ХОДА (перенесен из Balda.js)
    const playTurnSound = useCallback(() => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            const ctx = new AudioContext();
            const playTone = (freq, startTime, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0, startTime);
                gain.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
                gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(startTime);
                osc.stop(startTime + duration);
            };
            const now = ctx.currentTime;
            playTone(523.25, now, 0.2);       // До (C5)
            playTone(659.25, now + 0.15, 0.4); // Ми (E5)
        } catch(e) { console.warn('Audio failed', e); }
    }, []);


    // АРХИТЕКТУРНОЕ ИСПРАВЛЕНИЕ №2: Стабильный слушатель.
    useEffect(() => {
        if (!socket) return;
        sendAction('init');

        const handleEvent = (data) => {
            if (data.type === 'init') {
                hasDismissedRef.current = false;
                finalDataRef.current = null;
            }

            if (data.type === 'update' || data.type === 'sync' || data.type === 'success') {
                if (data.state) {
                    const prevState = stateRef.current;
                    const newState = data.state;

                    // ИМПЕРАТИВНАЯ ОБРАБОТКА ХОДА ОППОНЕНТА (Мгновенно)
                    const prevHistoryLen = prevState.lastMove?.length || 0;
                    const newHistoryLen = newState.lastMove?.length || 0;

                    if (newHistoryLen > prevHistoryLen) {
                        const lastMove = newState.lastMove[newState.lastMove.length - 1];
                        if (String(lastMove.userId) !== String(userId)) {
                            setOpponentAction(lastMove);
                            if (opponentTimerRef.current) clearTimeout(opponentTimerRef.current);
                            opponentTimerRef.current = setTimeout(() => setOpponentAction(null), 2000);
                        }
                    }

                    // ИМПЕРАТИВНАЯ ОБРАБОТКА ПЕРЕДАЧИ ХОДА (Мгновенно: Звук + Вспышка)
                    const isDuel = newState.mode === 'duel';
                    const wasMyTurn = isDuel ? String(prevState.duelData?.activePlayerId) === String(userId) : false;
                    const isNowMyTurn = isDuel ? String(newState.duelData?.activePlayerId) === String(userId) : false;
                    const isSpectatorNow = isDuel && !newState.duelData?.players?.find(p => String(p.id) === String(userId));

                    if (!wasMyTurn && isNowMyTurn && !newState.duelData?.isFinished && !isSpectatorNow) {
                        window.croco?.haptic?.('success');
                        playTurnSound();

                        setTurnToastKey(k => k + 1);
                        setFlashTurn(true);

                        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
                        flashTimerRef.current = setTimeout(() => setFlashTurn(false), 1000);
                    }

                    setState(prev => ({ ...prev, ...newState }));
                    setDuelData(newState.duelData);
                    setLastSyncTime(Date.now());

                    if ((isDuel && newState.duelData && !newState.duelData.isFinished) ||
                        (!isDuel && !newState.isFinished)) {
                        hasDismissedRef.current = false;
                        finalDataRef.current = null;
                    }

                    if (isDuel && newState.duelData) {
                        const me = newState.duelData.players.find(p => String(p.id) === userId);
                        isSpectator.current = !me;

                        if (newState.duelData.isFinished) {
                            if (!hasDismissedRef.current && !finalDataRef.current) {
                                const myPlayer = newState.duelData.players.find(p => String(p.id) === userId);
                                const fakeFinalData = {
                                    my: { score: myPlayer ? myPlayer.score : 0, rank: 'balda_rank_student', words:[] },
                                    leaderboard:[]
                                };
                                setFinalData(fakeFinalData);
                                setShowResults(true);
                                window.croco?.haptic?.('warning');
                            }
                        }
                    }
                    prevGridRef.current = newState.grid;
                }
            }

            if (data.type === 'success') {
                showFeedback('success', `+${data.points} ${data.word}`);
                if (onSuccessRef.current) onSuccessRef.current();
            } else if (data.type === 'reject') {
                const type = (data.reason === 'repeat_self' || data.reason === 'repeat_global') ? 'warning' : 'error';
                showFeedback(type, t(`arcade.balda_err_${data.reason}`));
                triggerShake(type);
            } else if (data.type === 'finish' || data.type === 'game_over') {
                if (data.state) {
                    setState(prev => ({ ...prev, ...data.state }));
                    if (data.state.duelData) setDuelData(data.state.duelData);
                }

                const parsedFinalData = data.finalData || data;
                if (data.result && !parsedFinalData.result) parsedFinalData.result = data.result;
                if (data.reason && !parsedFinalData.reason) parsedFinalData.reason = data.reason;

                const isSameResult = finalDataRef.current &&
                    finalDataRef.current.result === parsedFinalData.result &&
                    finalDataRef.current.reason === parsedFinalData.reason;

                if (!hasDismissedRef.current && !isSameResult) {
                    window.croco?.haptic?.('warning');
                    setFinalData(parsedFinalData);
                    setShowResults(true);
                }
            }
        };

        socket.on('arcade:event', handleEvent);
        return () => socket.off('arcade:event', handleEvent);

    // В зависимостях теперь только стабильные примитивы и функции
    // Сокет больше никогда не будет "моргать" в фоне.
    }, [socket, sendAction, userId, t, playTurnSound, setFinalData, setShowResults, showFeedback, triggerShake]);

    return {
        state, duelData, userId, isSpectator: isSpectator.current,
        statusMessage, shakeState, showResults, setShowResults, finalData,
        showDebug, lastSyncTime, localTick,
        sendAction, triggerShake, showFeedback,
        // Экспортируем новые пропсы для UI
        opponentAction, flashTurn, turnToastKey
    };
}